import type {
  BehaviorSignal,
  BehaviorSignalBus as IBehaviorSignalBus,
  BehaviorSignalListener,
  BehaviorSignalType,
} from '../contracts/signals';

/**
 * Barramento interno do módulo Behavior.
 * Isola falhas de listeners; IDs gerados por instância (sem module-level state).
 */
export class BehaviorSignalBus implements IBehaviorSignalBus {
  private readonly listeners = new Map<
    BehaviorSignalType,
    Set<BehaviorSignalListener>
  >();

  private cleared = false;

  private signalSeq = 0;

  publish(signal: BehaviorSignal): void {
    if (this.cleared) return;

    const set = this.listeners.get(signal.type);
    if (!set || set.size === 0) return;

    for (const listener of [...set]) {
      try {
        listener(signal);
      } catch {
        // Isolation: falha de listener não derruba o bus.
      }
    }
  }

  subscribe(
    type: BehaviorSignalType,
    listener: BehaviorSignalListener
  ): () => void {
    if (this.cleared) {
      return () => undefined;
    }

    let set = this.listeners.get(type);
    if (!set) {
      set = new Set();
      this.listeners.set(type, set);
    }
    set.add(listener);

    return () => {
      const current = this.listeners.get(type);
      if (!current) return;
      current.delete(listener);
      if (current.size === 0) {
        this.listeners.delete(type);
      }
    };
  }

  clear(): void {
    this.listeners.clear();
    this.cleared = true;
  }

  /** Permite reuso após destroy do módulo (reinit). */
  reset(): void {
    this.listeners.clear();
    this.cleared = false;
    this.signalSeq = 0;
  }

  listenerCount(type?: BehaviorSignalType): number {
    if (type) {
      return this.listeners.get(type)?.size ?? 0;
    }
    let total = 0;
    for (const set of this.listeners.values()) {
      total += set.size;
    }
    return total;
  }

  /** Cria sinal com ID isolado por instância do bus. */
  createSignal(
    type: BehaviorSignalType,
    source: string,
    data?: BehaviorSignal['data']
  ): BehaviorSignal {
    this.signalSeq += 1;
    return {
      id: `bsig_${this.signalSeq}_${Date.now()}`,
      type,
      timestamp: Date.now(),
      source,
      ...(data ? { data } : {}),
    };
  }
}
